import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  PatientDTO,
} from 'dtos';
import keyBy from 'lodash/keyBy';
import {
  useMedia,
} from 'core/hooks';
import {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import {
  useGetPatientInfo,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  useGetSummaryForEncounterId,
} from 'pages/Dashboard/pages/Billing/hooks';
import PatientInvoiceInfo from 'pages/Dashboard/pages/Billing/Charges/PatientInvoiceInfo';
import {
  createPortal,
} from 'react-dom';
import {
  isNilOrZero,
} from '@populateui/utils';
import ChartDetails from 'pages/Dashboard/pages/Charts/pages/Details';
import ChargeInfo from 'pages/Dashboard/pages/Billing/Charges/ChargeInfo';
import PatientHeader from 'pages/Dashboard/components/PatientHeader';
import ViewProvider, {
  ViewContext,
} from 'pages/Dashboard/contexts/viewContext';
import PanelExtension from 'core/components/PanelExtension';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  useSelectedEntitiesActions,
} from 'views/EMR/stores/entities';
import {
  billingEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import usePanelState from 'pages/Dashboard/hooks/usePanelState';
import {
  CHARGES_DEFAULT_TAB,
  ChargesPanelInfo,
  ChargesPanelTab,
  ChargesTabProps,
  nextClaimSelection,
  parseChargesTab,
  parseChartTab,
  parseClaimId,
  resolveChartTab,
} from 'pages/Dashboard/pages/Billing/Charges/chargesPanelInfo';

type Props = SidePanelComponentProps<ChargesPanelInfo>;

type OutletProps = Omit<SidePanelComponentProps, 'entity'> & {
  info: ChargesPanelInfo;
  tab: ChargesPanelTab;
  onTabChange: (tab: ChargesPanelTab) => void;
  claimId?: number;
  onClaimChange: (claimId?: number) => void;
};

const componentsByType: Readonly<
  Record<ChargesPanelTab, FC<ChargesTabProps>>> = Object.freeze({
    chart: ChartDetails,
    invoice: PatientInvoiceInfo,
    charge: ChargeInfo,
  });

const tabs = [{
  label: 'Charge',
  key: 'charge',
}, {
  label: 'Patient\'s Statement',
  key: 'invoice',
}, {
  label: 'Patient\'s Chart',
  key: 'chart',
}];

const tabsByType = Object.freeze(
  keyBy(tabs.map((tab, index) => ({ ...tab, index })), 'key'),
);

function PanelOutlet({
  info,
  tab,
  onTabChange,
  claimId,
  onClaimChange,
  onClose,
  slots,
  extensionRef,
}: OutletProps) {
  const {
    extensionExpanded,
    setExtensionExpanded,
    Extension,
  } = useContext(ViewContext);
  const { patientId, encounterId } = info;
  const {
    data: patient,
    isFetching: isLoading,
    isLoading: isLoadingPatient,
  } = useGetPatientInfo(patientId, { throwOnNoAccess: true });
  // Checks access to the encounter on every tab, not only on Charge
  const { isLoading: isLoadingEncounter } = useGetSummaryForEncounterId(encounterId);
  // Holds the tab back until both access checks settle, so it never shows before the unavailable panel
  const isCheckingAccess = isLoadingPatient || isLoadingEncounter;
  const { requestMedia } = useMedia();
  const Component = componentsByType[tab];

  const { setEntity, unsetEntity } = useSelectedEntitiesActions();

  useEffect(() => {
    if (!isNil(patient)) {
      setEntity(patient!, patient?.patientId!);
    }
    return () => {
      unsetEntity(patient?.patientId ?? 0);
    };
  }, [patient]);

  useEffect(() => {
    if (!isNilOrZero(patientId)) {
      requestMedia([patientId]);
    }
  }, [patientId]);

  return (
    <div className="flex flex-col justify-between bg-white rounded-xl overflow-hidden h-full w-full">
      <div className="flex flex-col flex-1 h-full w-[40rem]">
        <div className="w-full border-b border-gray-200">
          <div className="flex items-start justify-between w-full">
            <PatientHeader
              isLoading={isLoading}
              patient={(patient ?? {}) as Partial<PatientDTO>}
              onClose={onClose!}
              actions={slots?.actions}
            />
          </div>
          <NavigationTabs
            tabs={tabs}
            activeIndex={tabsByType[tab].index}
            className="px-4 py-2 !min-h-[unset]"
            onClick={(index) => { onTabChange(tabs[index].key as ChargesPanelTab); }}
          />
        </div>
        {isCheckingAccess
          ? (
            <div className="flex flex-1 items-center justify-center">
              <Spinner className="!h-10 !w-10" />
            </div>
          )
          : (
            <Component
              entity={{ ...info, id: info.encounterId, patient }}
              selectedClaimId={claimId}
              onSelectedClaimChange={onClaimChange}
              onClose={onClose}
            />
          )}
        {!isNil(extensionRef?.current) && (createPortal(
          (
            <PanelExtension className={clsx('transition-all', {
              'w-0': !extensionExpanded,
              '!w-[25rem] rounded-r-xl': extensionExpanded,
            })}
            >
              {!isNil(Extension) && (
                <Extension
                  patient={patient as PatientDTO}
                  className="bg-gray-100"
                  classes={{ list: 'px-4 ' }}
                  onClose={() => { setExtensionExpanded?.(null); }}
                />
              )}
            </PanelExtension>
          ), extensionRef!.current!,
        ))}
      </div>
    </div>
  );
}

export default function ChargesSidePanel({ entity, ...props }: Props) {
  const { encounterId, patientId } = entity;
  const { defaultChartTab, canChartTab } = useEMRAccess();
  const [tab, setTab] = usePanelState<ChargesPanelTab>({
    param: 'tab',
    parse: parseChargesTab,
    serialize: String,
    defaultValue: CHARGES_DEFAULT_TAB,
    initialValue: entity.tab,
  });
  const [chartTab, setChartTab] = usePanelState<string>({
    param: 'chartTab',
    parse: parseChartTab,
    serialize: String,
    defaultValue: defaultChartTab,
    initialValue: entity.chartTab,
  });
  // 0 means no claim is selected
  const [claimIdValue, setClaimIdValue] = usePanelState<number>({
    param: 'claimId',
    parse: parseClaimId,
    serialize: String,
    defaultValue: 0,
    initialValue: entity.claimId,
  });
  const claimId = claimIdValue || undefined;

  const setClaimId = useCallback((nextClaimId?: number) => {
    setClaimIdValue(nextClaimId ?? 0);
  }, [setClaimIdValue]);

  // Any top tab change drops the chart tab, so the Chart tab always opens on
  // the default and a chart tab left in a link without tab=chart is cleared
  const changeTab = useCallback((nextTab: ChargesPanelTab) => {
    setTab(nextTab);
    setChartTab(defaultChartTab);
  }, [setTab, setChartTab, defaultChartTab]);

  useEffect(() => {
    const unsubscribeFromChargesInfoChange = billingEventChannel.on('onChargesInfoChange', (payload) => {
      if (payload?.encounterId === encounterId) {
        setClaimId(nextClaimSelection(claimId, payload));
      }
    });

    return () => {
      unsubscribeFromChargesInfoChange();
    };
  }, [encounterId, claimId, setClaimId]);

  return (
    <ViewProvider
      tab={resolveChartTab(tab === 'chart' ? chartTab : undefined, { defaultChartTab, canChartTab })}
      onTabChange={setChartTab}
      isPanel
      entityId={patientId}
    >
      <PanelOutlet
        {...props}
        info={entity}
        tab={tab}
        onTabChange={changeTab}
        claimId={claimId}
        onClaimChange={setClaimId}
      />
    </ViewProvider>
  );
}
